import React, { useState } from 'react';
import { signIn } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api'; // Import the API utility

const SignInView = ({
  email, setEmail, password, setPassword,
  setIsNewUser, setMessage, setIsAuthenticated
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate(); // Add useNavigate hook

  const handleSignIn = async () => {
    if (!email.trim()) {
      setMessage("Please enter your email.");
      return;
    }
    if (!password.trim()) {
      setMessage("Please enter your password.");
      return;
    }
  
    try {
      setIsLoading(true);
      setMessage("");
      
      const response = await signIn({
        username: email,
        password,
      });
      
      // Get tokens from the sign-in response
      const tokens = response?.tokens;
      
      if (tokens?.idToken?.toString()) {
        // Store auth token in session storage
        sessionStorage.setItem('authToken', tokens.idToken.toString());
        
        // Store user ID if available from ID token
        const userId = tokens.idToken.payload?.sub || 
                      tokens.accessToken?.payload?.sub || null;
                      
        if (userId) {
          sessionStorage.setItem('userId', userId);
          
          // Immediately check billing status
          try {
            const billingResponse = await apiPost('/get-payment-methods', { userId });
            const hasPaymentMethods = billingResponse.paymentMethods && 
                                    billingResponse.paymentMethods.length > 0;
                                    
            // Set authentication state
            setIsAuthenticated(true);
            
            // If no payment methods, redirect to billing setup immediately
            if (!hasPaymentMethods) {
              navigate('/billing-setup');
            } else {
              navigate('/dashboard');
            }
          } catch (error) {
            console.error("Error checking billing:", error);
            // If billing check fails, still authenticate but redirect to billing setup as a precaution
            setIsAuthenticated(true);
            navigate('/billing-setup');
          }
        } else {
          // Fallback if we couldn't get userId
          setIsAuthenticated(true);
        }
      } else {
        console.log("No tokens found in sign-in response:", response);
        setIsAuthenticated(true); // Still set authenticated in case tokens structure changed
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      setMessage(error.message || "An error occurred during sign-in.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signin-card" id="signinCard">
      <h2 className="signin-title" id="signinTitle">Log In</h2>
      
      <div className="signin-form" id="signinForm">
        <div className="signin-form-group" id="signinEmailGroup">
          <label className="signin-label" id="signinEmailLabel">Email</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            className="signin-input signin-email-input"
            id="signinEmailInput"
            placeholder="Enter your email"
          />
        </div>
        
        <div className="signin-form-group" id="signinPasswordGroup">
          <label className="signin-label" id="signinPasswordLabel">Password</label>
          <input 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            className="signin-input signin-password-input"
            id="signinPasswordInput"
            placeholder="Enter your password"
          />
        </div>
        
        <button 
          onClick={handleSignIn}
          className={`signin-btn ${isLoading ? 'signin-btn-loading' : ''}`}
          id="signinSubmitBtn"
          disabled={isLoading}
        >
          {isLoading ? "Signing in..." : "Log In"}
        </button>

        <button 
          onClick={() => navigate('/forgot-password')}
          className="forgot-password-link"
          id="forgotPasswordLink"
        >
          Forgot Password?
        </button>
        
        <button 
          onClick={() => setIsNewUser(true)}
          className="signin-switch-btn"
          id="switchToSignupBtn"
        >
          Don't have an account? Sign Up
        </button>
        
        {/* ADDED: Privacy Policy and Terms Links */}
        <div className="signin-policy-links" id="signinPolicyLinks" style={{ 
          marginTop: '20px', 
          paddingTop: '16px', 
          borderTop: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <p style={{ 
            fontSize: '12px', 
            color: '#6c757d', 
            margin: '0 0 8px 0',
            lineHeight: '1.4'
          }}>
            By signing in, you agree to our{' '}
            <a 
              href="https://smack.fm/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            {' '}and{' '}
            <a 
              href="https://smack.fm/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline' }}
            >
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignInView;